import { acquireToken } from '@alixpartners/ui-utils';
import { BlockBlobClient } from '@azure/storage-blob';
import merge from 'lodash/merge';
import {
  IIndustryAllocationProvider,
  IIndustryBlend,
  ISettings,
  IStoredQueryItem,
} from './types';
import ApolloClient from 'apollo-boost';
import queryString from 'query-string';
import {
  ITransactionItem,
  SpendTypeEnum,
  IRuleItem,
} from 'components/Classification';
import {
  ISpendBenchmarksParams,
  IHeadcountBenchmarksParams,
  ISpendMapParams,
} from 'components/Benchmarks/types';
import {
  DimensionTypeEnum,
  IAllocatedYear,
} from 'components/SpendAllocation/types';
import {
  IApplicationSpendResp,
  ISignalrNegotiationResult,
} from 'views/SpendAllocation/ProductAllocation/types';
import {
  IApplicationSpend,
  IProductResponse,
  ISpendByProductResponse,
} from 'views/Products/types';

const { REACT_APP_API_URL } = process.env;

const addAuthHeaders = (token: string, options = { headers: {} }) => ({
  ...options,
  headers: {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  },
});

const parseTextResponse = (textResponse: string) => {
  try {
    const jsonResponse = JSON.parse(textResponse);
    return jsonResponse;
  } catch {
    return textResponse;
  }
};

class ApiError extends Error {
  statusCode: number;
  constructor(statusCode: number, message: string) {
    super(message);
    this.statusCode = statusCode;
    this.message = message;
    Error.captureStackTrace(this, ApiError);
  }
}

const jsonRequest = async (url: string, options = {}): Promise<any> => {
  const defaultOptions = {
    headers: { 'content-type': 'application/json' },
  };
  try {
    const response = await fetch(
      `${REACT_APP_API_URL}${url}`,
      merge(defaultOptions, options)
    );
    const textResponse = await response.text();
    const parserdResponse = parseTextResponse(textResponse);

    if (!response.ok) {
      throw new ApiError(
        response.status,
        parserdResponse.hasOwnProperty('errorMessage')
          ? parserdResponse.errorMessage
          : JSON.parse(textResponse)
      );
    }
    return parserdResponse;
  } catch (error) {
    if (error.statusCode === 403) {
      window.history.replaceState(
        {
          state: {
            aadUrl: error.message && error.message.aadUrl,
            aadName: error.message && error.message.aadName,
          },
        },
        '',
        '/accessdenied'
      );
      window.location.replace('/accessdenied');
    } else {
      throw new ApiError(error.statusCode, error.message);
    }
  }
};

const requestWithToken = (url: string, defaults: any, options: any) => {
  return acquireToken().then((token) => {
    if (!token) {
      throw new Error(`ServerError (401)`);
    }
    const authDefaults = addAuthHeaders(token, defaults);
    return jsonRequest(url, {
      ...authDefaults,
      ...options,
    });
  });
};

export const getRequest = (url: string, options = {}) => {
  let defaults = { method: 'GET' };
  return requestWithToken(url, defaults, options);
};

export const postRequest = (url: string, options = {}) => {
  let defaults = { method: 'POST' };
  return requestWithToken(url, defaults, options);
};

export const putRequest = (url: string, options = {}) => {
  let defaults = { method: 'PUT' };
  return requestWithToken(url, defaults, options);
};

export const patchRequest = (url: string, options = {}) => {
  let defaults = { method: 'PATCH' };
  return requestWithToken(url, defaults, options);
};

export const deleteRequest = (url: string, options = {}) => {
  let defaults = { method: 'DELETE' };
  return requestWithToken(url, defaults, options);
};

export const fetchSettings = () => {
  return getRequest('/api/v1/config/settings');
};

export const setSettings = (settings: ISettings) => {
  const options = {
    body: JSON.stringify(settings),
  };
  return postRequest('/api/v1/config/settings', options);
};

export const fetchFilters = () => {
  return getRequest('/api/v1/config/filters');
};

export const fetchSpendByContribution = (dimension: string, query?: string) => {
  return getRequest(`/api/v1/spends/spendbycontribution/${dimension}?${query}`);
};

export const fetchHeadlines = (query?: string) => {
  return getRequest(`/api/v1/spends/headlines?${query}`);
};

export const fetchYearOnYear = (query?: string) => {
  return getRequest(`/api/v1/spends/yoychart?${query}`);
};

export const fetchBreakdownChart = (query?: string) => {
  return getRequest(`/api/v1/spends/breakdownbychart?${query}`);
};

export const fetchTopVendors = (query?: string) => {
  return getRequest(`/api/v1/spends/topvendors?${query}`);
};

export const fetchTopItems = (query?: string) => {
  return getRequest(`/api/v1/spends/topitems?${query}`);
};

export const fetchHeadcountSpend = (query?: string) => {
  return getRequest(`/api/v1/spends/headcountspend?${query}`);
};

export const fetchNotifications = () => {
  return getRequest('/api/notifications/my');
};

export const fetchFiles = () => {
  return getRequest('/api/v1/files');
};

export const deleteAllFiles = () => {
  return deleteRequest('/api/v1/files');
};

export const deleteFile = (id: number) => {
  return deleteRequest(`/api/v1/files/${id}`);
};

export const getFile = (id: number) => {
  return getRequest(`/api/v1/files/${id}`);
};

export const getFileStatus = (id: string) => {
  return getRequest(`/api/v1/files/${id}/status`);
};

export const getUploadPath = (fileType: string) => {
  return postRequest(`/api/v1/files/${fileType}`);
};

export const uploadExcelFile = (
  file: any,
  fileType: string,
  onFileCreated: (data: any) => void,
  onError: (err: any) => void
) => {
  return createExcelFile(file.name, fileType).then((data: any) => {
    onFileCreated(data);
    return data;
  }, onError);
};

export function confirmExcelFile(id: string) {
  return patchRequest(`/api/v1/files/${id}`);
}

/**
 * Upload data using the Azure SDK to ensure that large files
 * are uploaded in chunks when files are larger than 256mb.
 *
 * @param {string} url
 * @param {blob} file
 */
export function uploadFile(url: string, file: any) {
  const blobClient = new BlockBlobClient(url);
  return blobClient.uploadData(file);
}

export function createExcelFile(fileName: string, fileType: string) {
  const options = {
    body: JSON.stringify({ fileName }),
  };
  return postRequest(`/api/v1/files/${fileType}`, options);
}

export const markNotificationsRead = (ids: string | string[]) => {
  const url = '/api/notifications/my';
  const options = {
    body: JSON.stringify({
      newState: 'Read',
      ids: Array.isArray(ids) ? ids : [ids],
    }),
  };

  return patchRequest(url, options);
};

export const archiveNotifications = (ids: string | string[]) => {
  const url = '/api/notifications/my';
  const options = {
    body: JSON.stringify({
      newState: 'Archived',
      ids: Array.isArray(ids) ? ids : [ids],
    }),
  };

  return patchRequest(url, options);
};

export async function getGraphQLClientWithToken() {
  const token = await acquireToken();
  const { REACT_APP_API_URL } = process.env;

  if (!token) {
    throw new Error(`ServerError (401)`);
  }

  const authDefaults = addAuthHeaders(token);
  const client = new ApolloClient({
    uri: `${REACT_APP_API_URL}/api/graphql`,
    ...authDefaults,
  });

  return client;
}

export const getTaxonomy = (spendType?: string) => {
  const url = `/api/v1/taxonomy${spendType ? `/${spendType}` : ''}`;

  return getRequest(url);
};

export const getTransactionsInfo = (spendType: string) => {
  const url = `/api/v1/spends/unclassified/${spendType}`;

  return getRequest(url);
};

export const saveTransactions = (
  spendType: SpendTypeEnum,
  data: ITransactionItem[]
) => {
  const url = `/api/v1/spends/classify/${spendType}`;
  const options = {
    body: JSON.stringify(data),
  };

  return patchRequest(url, options);
};

export const saveRules = (spendType: SpendTypeEnum, data: IRuleItem[]) => {
  const url = `/api/v1/classificationrules/${spendType}`;
  const options = {
    body: JSON.stringify(data),
  };

  return postRequest(url, options);
};

export const publishSpendData = (spendType: string) => {
  const url = `/api/v1/publish/${spendType}`;

  return postRequest(url);
};

export const getClassificationRules = () => {
  const url = `/api/v1/classificationrules`;

  return getRequest(url);
};

export const deleteAllClassificationRules = (spendType: string) => {
  const url = `/api/v1/classificationrules/${spendType}`;

  return deleteRequest(url);
};

export const getPermissions = () => {
  return getRequest('/api/mypermissions');
};

export const getIndustryBlends = () => {
  return getRequest('/api/v1/industryblends');
};

export const createIndustryBlend = (industryBlend: IIndustryBlend) => {
  return postRequest('/api/v1/industryblends', {
    body: JSON.stringify(industryBlend),
  });
};

export const updateIndustryBlend = (industryBlend: IIndustryBlend) => {
  return putRequest('/api/v1/industryblends', {
    body: JSON.stringify(industryBlend),
  });
};

export const deleteIndustryBlend = (id: string | number) => {
  return deleteRequest(`/api/v1/industryblends/${id}`);
};

export const patchIndustryBlend = (id: string | number) => {
  return patchRequest(`/api/v1/industryblends/${id}/activate`);
};

export const getApIndustries = () => {
  return getRequest('/api/v1/industryblends/industries');
};

export const getProviderIndustries = () => {
  return getRequest('/api/v1/industryblends/providerIndustries');
};

export const getBenchmarksYears = () => {
  return getRequest('/api/v1/benchmarks/years');
};

export const getSignalrNegotiationResult =
  (): Promise<ISignalrNegotiationResult> => {
    return getRequest('/api/v1/signalr/negotiate');
  };

export const calculateBenchmarks = (industryBlend: IIndustryBlend) => {
  return postRequest('/api/v1/benchmarks/recalculatebyblend', {
    body: JSON.stringify(industryBlend),
  });
};

export const checkDataAvailability = ({
  year,
  providerIndustries,
}: {
  year: number;
  providerIndustries: IIndustryAllocationProvider[];
}) => {
  return postRequest('/api/v1/benchmarks/checkDataAvailability', {
    body: JSON.stringify({ providerIndustries, year }),
  });
};

export const checkDataAvailabilityByIndustryBlend = (
  industryBlend: IIndustryBlend
) => {
  return postRequest(
    '​/api/v1/benchmarks/checkDataAvailabilityByIndustryBlend',
    {
      body: JSON.stringify(industryBlend),
    }
  );
};

export const getHeadcountBenchmarks = (params: IHeadcountBenchmarksParams) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/benchmarkcharts/headcount?${query}`);
};

export const getSpendBenchmarks = (params: ISpendBenchmarksParams) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/benchmarkcharts/spend?${query}`);
};

export const getJobSpend = (query?: string) => {
  return getRequest(`/api/v1/spends/headcountspendtable?${query}`);
};

export const personnelExport = (params: {
  startDate: string;
  endDate: string;
}) => {
  const query = queryString.stringify(params);
  return getRequest(`/api/v1/spends/personnel/export?${query}`);
};

export const thirdPartyExport = (params: {
  startDate: string;
  endDate: string;
}) => {
  const query = queryString.stringify(params);
  return getRequest(`/api/v1/spends/thirdParty/export?${query}`);
};

export const getPersonnel = (params?: {
  startDate?: string;
  endDate?: string;
}) => {
  const query = queryString.stringify(params || {});
  return getRequest(`/api/v1/spends/personnel?${query}`);
};

export const getThirdParty = (params?: {
  startDate?: string;
  endDate?: string;
}) => {
  const query = queryString.stringify(params || {});
  return getRequest(`/api/v1/spends/thirdParty?${query}`);
};

export const downloadSpendFile = (fileName: string) => {
  return getRequest(`/api/v1/files/export/${fileName}`);
};

export const getPersonnelMapData = (params: ISpendMapParams) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/spends/map?${query}`);
};

export const exportSpendData = (dimension: string, query: string) => {
  return getRequest(
    `/api/v1/spend/dataexport/spendsummary/${dimension}/?${query}`
  );
};

export const exportSpendBenchmarksData = (params: any) => {
  const query = queryString.stringify(params);

  return getRequest(
    `/api/v1/benchmarks/dataexport/spendchartdataexport?${query}`
  );
};

export const exportHeadcountBenchmarksData = (params: any) => {
  const query = queryString.stringify(params);

  return getRequest(
    `/api/v1/benchmarks/dataexport/headcountchartdataexport?${query}`
  );
};

export const exportSpendBenchmarksTableData = (params: any) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/benchmarks/dataexport/topspend?${query}`);
};

export const exportHeadcountBenchmarksTableData = (params: any) => {
  const query = queryString.stringify(params);

  return getRequest(
    `/api/v1/benchmarks/dataexport/headcountspendbreakdown?${query}`
  );
};

export const exportSpendYoYData = (query?: string) =>
  getRequest(`/api/v1/spend/dataexport/yoychart?${query}`);

export const exportSpendTopVendorsData = (query?: string) =>
  getRequest(`/api/v1/spend/dataexport/topvendors?${query}`);

export const exportSpendBreakdownChartData = (
  dimension: string,
  query?: string
) => getRequest(`/api/v1/spend/dataexport/breakdown/${dimension}?${query}`);

export const exportSpendHeadlineData = (query?: string) =>
  getRequest(`/api/v1/spend/dataexport/headlines?${query}`);

export const exportSpendHeadcountData = (spendView: string, query?: string) =>
  getRequest(
    `/api/v1/spend/dataexport/headcountspend?${query}&spendView=${spendView}`
  );

export const exportSpendHeadcountTableData = (
  spendView: string,
  query?: string
) =>
  getRequest(
    `/api/v1/spend/dataexport/headcountspendtable?${query}&spendView=${spendView}`
  );

export const exportSpendBreakdownBusinessUnitData = (
  dimension: string,
  query?: string
) =>
  getRequest(
    `/api/v1/spend/dataexport/breakdownbybusinessunit/${dimension}?${query}`
  );

export const exportSpendBreakdownGeographyData = (
  dimension: string,
  query?: string
) =>
  getRequest(
    `/api/v1/spend/dataexport/breakdownbygeography/${dimension}?${query}`
  );

export const exportSpendTopThirdPartyData = (query?: string) =>
  getRequest(`/api/v1/spend/dataexport/topthirdpartyspend/?${query}`);

export const getApplications = () => {
  return getRequest('/api/v1/applications');
};

export const getDimensions = (
  params: {
    startDate: string;
    endDate: string;
    contributions: string;
  },
  dimension: DimensionTypeEnum
) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/spends/groupedBy/${dimension}?${query}`);
};

export const getAllocations = (params: {
  contribution: string;
  dimension?: DimensionTypeEnum;
}) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/applications/allocations?${query}`);
};

export const updateAllocations = (
  allocations: IAllocatedYear[],
  contribution: string
) => {
  const options = {
    body: JSON.stringify(allocations),
  };

  return postRequest(
    `/api/v1/applications/${contribution}/allocations`,
    options
  );
};

export const getApplicationCharts = (query?: string) => {
  return getRequest(`/api/v1/applications/chart/?${query}`);
};

export const exportApplicationHeroData = (query?: string) => {
  return getRequest(`/api/v1/applications/export/herochart?${query}`);
};

export const exportApplicationContributionData = (query?: string) => {
  return getRequest(`/api/v1/applications/export/contribution?${query}`);
};

export const exportApplicationPieChartData = (query?: string) => {
  return getRequest(`/api/v1/applications/export/piechart?${query}`);
};

export const exportApplicationSummaryData = (query?: string) => {
  return getRequest(`​/api/v1/applications/export/summary?${query}`);
};

export const runAutoClassification = () => {
  return postRequest('/api/v1/classification/runauto');
};

export const checkApplicationAllocation = () => {
  return getRequest('/api/v1/applications/checkallocations');
};

export const getApplicationSpend = (params: {
  startDate: string;
  endDate: string;
  contributions: string;
}): Promise<IApplicationSpendResp> => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/applications/spend?${query}`);
};

export const getProducts = (params: { startDate: string; endDate: string }) => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/products?${query}`);
};

export const getProductAppAllocations = (params: {
  contribution: string;
}): Promise<IAllocatedYear[]> => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/products/app/allocations?${query}`);
};

export const getProductNonAppAllocations = (params: {
  contribution: string;
}): Promise<IAllocatedYear[]> => {
  const query = queryString.stringify(params);

  return getRequest(`/api/v1/products/nonApp/allocations?${query}`);
};

export const updateAppProductAllocations = (
  allocations: IAllocatedYear[],
  contribution: string
) => {
  const options = {
    body: JSON.stringify(allocations),
  };

  return postRequest(
    `/api/v1/products/${contribution}/app/allocations`,
    options
  );
};

export const updateNonAppProductAllocations = (
  allocations: IAllocatedYear[],
  contribution: string
) => {
  const options = {
    body: JSON.stringify(allocations),
  };

  return postRequest(
    `/api/v1/products/${contribution}/nonApp/allocations`,
    options
  );
};

export const getStoredQuery = (id: string): Promise<IStoredQueryItem[]> => {
  return getRequest(`/api/storedqueries/${id}`);
};

export const createStoredQuery = (
  params: IStoredQueryItem[]
): Promise<{ id: number }> => {
  const options = {
    body: JSON.stringify(params),
  };

  return postRequest('/api/storedqueries', options);
};

export const getProductsSpend = (query: string): Promise<IProductResponse> => {
  return getRequest(`/api/v1/products/spend?${query}`);
};

export const getSpendByProduct = ({
  query,
  productId,
}: {
  query: string;
  productId: number;
}): Promise<ISpendByProductResponse> => {
  return getRequest(
    `/api/v1/products/spendByProduct?${query}&productId=${productId}`
  );
};

export const getSpendByApplicationAndProduct = ({
  applicationId,
  productId,
  query,
}: {
  applicationId: number;
  productId: number;
  query: string;
}): Promise<IApplicationSpend[]> => {
  return getRequest(
    `/api/v1/products/spendByProductApplication?${query}&productId=${productId}&applicationId=${applicationId}`
  );
};

export const exportProducts = (
  query: string,
  params: {
    spendViewType: string;
  }
) => {
  const queryParams = queryString.stringify(params);

  return getRequest(`/api/v1/productexport/herochart?${query}&${queryParams}`);
};

export const exportProductsAppSpend = (
  query: string,
  params: {
    spendViewType: string;
    productId: number;
    contributions?: string;
  }
) => {
  const queryParams = queryString.stringify(params);
  return getRequest(
    `/api/v1/productexport/productappspend?${query}&${queryParams}`
  );
};

export const exportProductsAppAllocationSpend = (
  query: string,
  params: {
    spendViewType: string;
    productId: number;
    applicationId: number;
    l1ServiceTowerName?: string;
    contributions?: string;
  }
) => {
  const queryParams = queryString.stringify(params);
  return getRequest(
    `/api/v1/productexport/productappallocationspend?${query}&${queryParams}`
  );
};
